import React, { useEffect } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import type { EventNameNavForStandaloneHeaderProps } from '../HeaderV2/components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventPageType } from '@graphql/generated-base';
import type { GuestSiteEventDesign } from '../../../layout.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { LazyPhotoBackgroundLayout } from '../HeaderV2/layouts/PhotoBackground.lazy';
import { LazyPhotoBackgroundMonogramLayout } from '../HeaderV2/layouts/PhotoBackgroundMonogram.lazy';
import { LazyVideoBackgroundLayout } from '../HeaderV2/layouts/VideoBackground.lazy';
import { LazyVideoBackgroundMonogramLayout } from '../HeaderV2/layouts/VideoBackgroundMonogram.lazy';
import { LazyVideoBackgroundHotfixLayout } from '../HeaderV2/layouts/VideoBackgroundHotfix.lazy';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { useApplicatorSpriteGuard } from './useApplicatorSpriteGuard';

type StandaloneWebsiteHeaderProps = Readonly<EventNameNavForStandaloneHeaderProps> &
  Readonly<{
    websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
  }>;

type VideoBackgroundPayload = Readonly<{
  [eventId: string]: {
    videoId?: string;
    mobileVideoId?: string;
    fallbackImageUrl?: string;
  };
}>;

const isPreview = isInIframe();

/** The hero layouts this component renders — the ones that push the frame below the fold. */
const STANDALONE_HERO_LAYOUTS: ReadonlyArray<PhotoPresentationLayout> = ['photoBackground', 'photoBackgroundMonogram', 'videoBackground', 'videoBackgroundMonogram'];

export const StandaloneWebsiteHeader = (props: StandaloneWebsiteHeaderProps) => {
  const { websiteHeader, ...restProps } = props;
  const { eventInfo } = useEventInfo();
  const [isVideoBackgroundEnabled, , , payload] = useIsFeatureEnabled('websiteVideoBackgroundEnabled');
  const videoBackgroundPayload = eventInfo?.eventId && payload ? (payload as VideoBackgroundPayload)[eventInfo.eventId] : null;

  const headerPhoto = websiteHeader.headerPhoto;
  const showPlaceholder = isPreview && !!headerPhoto.presentationLayout;

  useApplicatorSpriteGuard(STANDALONE_HERO_LAYOUTS.includes(headerPhoto.presentationLayout));

  useEffect(() => {
    if (headerPhoto.presentationLayout) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [headerPhoto.presentationLayout]);

  switch (headerPhoto.presentationLayout) {
    case 'photoBackground':
      // If video background is enabled and there is a video background payload, render the video background layout
      // Otherwise, render the photo background layout
      // We recently changed the feature flag to be enabled by default,
      // so we need to check if the video background payload is present
      return isVideoBackgroundEnabled && videoBackgroundPayload ? (
        <LazyVideoBackgroundHotfixLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
          videoId={videoBackgroundPayload?.videoId || ''}
          mobileVideoId={videoBackgroundPayload.mobileVideoId}
          fallbackImageUrl={videoBackgroundPayload?.fallbackImageUrl || ''}
        />
      ) : (
        <LazyPhotoBackgroundLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
        />
      );
    case 'photoBackgroundMonogram':
      return (
        <LazyPhotoBackgroundMonogramLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
        />
      );
    case 'videoBackground':
      return (
        <LazyVideoBackgroundLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    case 'videoBackgroundMonogram':
      return (
        <LazyVideoBackgroundMonogramLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    default:
      return null;
  }
};

StandaloneWebsiteHeader.displayName = 'StandaloneWebsiteHeader';
